/**
 * witness.ts — the witness's own printed fields, written where they are read.
 *
 * `contract_witnesses` IS A STANDALONE TABLE, NOT A JUNCTION: `contracts.witness_id` points at
 * it, so one witness can stand on several contracts and this write reaches every one of them.
 * That is the intended behaviour — a witness's phone number is the witness's, not the contract's
 * — and it is why the update is keyed on the WITNESS id rather than on the contract.
 *
 * WHAT THE TWO FIELDS ARE FOR. The witness agreement prints the witness's name, phone AND address,
 * and it is the only instrument that names them at all [D-B18]: the hire purchase agreement's v3
 * body dropped the witness signature group along with `witness.full_name` and `witness.phone`. The
 * create form used to accept a name alone, so a witness could satisfy the picker and then block a
 * send for a reason no surface named — which is the defect the parties block, and this write, exist
 * to close. `createWitness` below closes the other half: the rule now lives on the write rather
 * than on a form (`witnessDraft.ts`).
 *
 * RLS is admin-only (`FOR ALL`), which is correct for a legal party: nobody outside the office
 * amends the person who witnessed an instrument.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

import {
    validateWitnessDraft,
    witnessContactPatch,
    type WitnessDraft,
    type WitnessOption,
} from './witnessDraft';

export type { WitnessDraft, WitnessOption } from './witnessDraft';

/**
 * `contract_witnesses.relationship` arrived with the schedule restructure's backend migration, and
 * the generated types predate it — regenerate once it has applied and these three aliases collapse
 * back into the plain `Tables<…>` helpers.
 *
 * `alt_phone` USED TO STAND BESIDE IT AND IS GONE, column and merge key alike. It was never asked
 * for on any surface an admin fills in first, so every witness row carried NULL and the witness
 * agreement's merge pre-flight reported `witness.alt_phone` outstanding on a document nobody could
 * complete. The owner ruled it out end to end rather than made fillable.
 *
 * THE INTERSECTION IS THE POINT, NOT A CAST. `as any` at each call site would take the type
 * checking off the whole payload — the name, the id, the columns that DO exist — to make one field
 * typeable; naming the field keeps every other one checked, and the day the regen lands the
 * deletion is a mechanical one.
 */
type WitnessColumns = { relationship?: string | null };
type WitnessRecord = Tables<'contract_witnesses'> & WitnessColumns;
type WitnessInsert = TablesInsert<'contract_witnesses'> & WitnessColumns;
type WitnessUpdate = TablesUpdate<'contract_witnesses'> & WitnessColumns;

/** Empty means empty — a cleared field is stored as NULL, not as `''`. */
function trimmed(value: string | undefined): string | null {
    const text = (value ?? '').trim();
    return text === '' ? null : text;
}

/**
 * ONE KEY FOR ONE LIST. The selector on the preparation form and the setup canvas's picker read
 * the same people; two reads under the same key with two different row shapes is a cache that
 * hands whichever surface mounts second the other one's data.
 */
export const WITNESSES_KEY = ['witnesses'] as const;

type WitnessRow = WitnessRecord & { contracts: { id: number }[] | null };

/**
 * `select('*')` IS LOAD-BEARING, NOT LAZINESS — the same rule `data/roadworthiness.ts` states, and
 * it now cuts in BOTH directions. `relationship` ships with the schedule restructure's migration;
 * naming it in the select would fail this read with 42703 on every environment that migration has
 * not reached yet, and this read is what draws the witness picker on the setup page. The same star
 * is what lets `alt_phone` LEAVE without this read caring: a select that had named the column would
 * 42703 the moment the drop promoted, and a star select returns whatever columns exist.
 */
export async function fetchWitnesses(): Promise<WitnessOption[]> {
    const { data, error } = await supabase
        .from('contract_witnesses')
        .select('*, contracts(id)')
        .order('name', { ascending: true });
    if (error) throw error;

    return ((data ?? []) as WitnessRow[]).map((row) => ({
        id: row.id,
        name: row.name,
        phone: row.phone,
        address: row.address,
        relationship: row.relationship ?? null,
        contractCount: row.contracts?.length ?? 0,
    }));
}

/**
 * Create a witness, or refuse in the create panel's own words.
 *
 * THE VALIDATION IS THE WRITE'S, NOT THE FORM'S. A disabled button is a courtesy; this is the
 * guarantee — every path that reaches `contract_witnesses` through the app comes through here,
 * and a name-only row cannot be inserted from any of them.
 */
export async function createWitness(draft: WitnessDraft): Promise<number> {
    const refusal = validateWitnessDraft(draft);
    if (refusal) throw new Error(refusal);

    const relationship = trimmed(draft.relationship);

    /**
     * THE NEW COLUMN IS NAMED ONLY WHEN IT CARRIES SOMETHING, and that is the whole degradation
     * story for creating a witness. An omitted column and a column set to NULL are the same row;
     * they are not the same request, and against a server whose migration has not landed the
     * second is a 42703 that refuses a witness nobody was trying to give a relationship to.
     * Omitting it means creating a witness the old way still works exactly as it did, and only an
     * admin who actually fills the field can meet the error.
     */
    const payload: WitnessInsert = {
        name: draft.name.trim(),
        phone: trimmed(draft.phone),
        address: trimmed(draft.address),
        ...(relationship === null ? {} : { relationship }),
    };

    const { data, error } = await supabase
        .from('contract_witnesses')
        .insert(payload)
        .select('id')
        .single();
    if (error) throw error;
    return data.id;
}

/**
 * The witness's own fields, saved from the parties block's commit bar.
 *
 * THE UPDATE NAMES THE TWO SNAPSHOT-BACKED COLUMNS UNCONDITIONALLY, because clearing is a thing
 * this write has to be able to do: a phone typed in error is corrected by emptying the field, and
 * a column left out of an UPDATE keeps its old value. It names `relationship` only when the caller
 * has a value to state for it — see `witnessContactPatch`, which holds the rule and the reason:
 * that one is read from the witness LIST rather than from the contract snapshot, and a Save fired
 * before that list resolves would otherwise write NULL over a recorded value.
 *
 * The create path's own asymmetry is a different one and still stands: it omits the new column so a
 * witness can be created against a server the migration has not reached.
 */
export async function saveWitnessContact({
    witnessId,
    phone,
    address,
    relationship,
}: {
    witnessId: number | null;
    phone: string;
    address: string;
    /** Undefined means "the list has not answered for this yet" — the column is left alone. */
    relationship?: string;
}) {
    if (witnessId === null) throw new Error('Select a witness before recording their details.');

    const patch: WitnessUpdate = witnessContactPatch({ phone, address, relationship });

    const { error } = await supabase.from('contract_witnesses').update(patch).eq('id', witnessId);
    if (error) throw error;
}
